import type { RefObject } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { IconButton, Menu, MenuItem, MenuTrigger, Popover } from '@launchpad-ui/components';

import { DataTableActionContent } from './DataTableActionContent';
import type { DataTableAction } from './types';

const focusFirstItem = (menu: HTMLDivElement) =>
  menu.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus();

const moveFocusOffMenu = (menu: HTMLDivElement | null) => {
  if (!menu) {
    return;
  }
  const onFocus = (event: FocusEvent) => {
    if (event.target === menu) {
      focusFirstItem(menu);
    }
  };
  menu.addEventListener('focus', onFocus);
  if (document.activeElement === menu) {
    focusFirstItem(menu);
  }
  return () => menu.removeEventListener('focus', onFocus);
};

type Props<T> = {
  actions: Array<DataTableAction<T>>;
  row: T;
  rowLabel: string;

  columnName?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  triggerRef?: RefObject<Element | null>;
};

export function DataTableCellActions<T>({
  actions,
  columnName,
  row,
  rowLabel,
  isOpen,
  onOpenChange,
  triggerRef,
}: Props<T>) {
  if (actions.length === 0) {
    return null;
  }

  const label = columnName
    ? msg('ui.dataTable.cellActions.columnRowLabel', { columnName, rowLabel })
    : msg('ui.dataTable.cellActions.rowLabel', { rowLabel });
  const items = actions.map((action) => (
    <MenuItem
      key={action.id}
      id={action.id}
      isDisabled={action.isDisabled}
      variant={action.variant}
      onAction={() => {
        action.onAction(row);
        if (triggerRef) {
          onOpenChange?.(false);
        }
      }}
    >
      {action.icon ? <DataTableActionContent icon={action.icon}>{action.label}</DataTableActionContent> : action.label}
    </MenuItem>
  ));

  if (triggerRef) {
    return (
      <Popover triggerRef={triggerRef} isOpen={isOpen} onOpenChange={onOpenChange} placement="bottom start">
        <Menu ref={moveFocusOffMenu} aria-label={label} autoFocus="first">
          {items}
        </Menu>
      </Popover>
    );
  }

  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <IconButton aria-label={label} icon="more-horiz" size="small" variant="minimal" />
      <Popover placement="bottom end">
        <Menu>{items}</Menu>
      </Popover>
    </MenuTrigger>
  );
}
